import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";

const SOCIAL = [
  { label: "YouTube", href: "https://www.youtube.com/@Caffei_N", icon: "📺" },
  { label: "Facebook", href: "https://facebook.com/caffeiN0w0", icon: "👥" },
  { label: "Pinterest", href: "https://www.pinterest.com/caffei_n", icon: "📌" },
];

export default function Study() {
  const [posts, setPosts] = useState([]);
  const { dark } = useTheme();

  useEffect(() => {
    fetch("/api/posts")
      .then((r) => r.json())
      .then(setPosts)
      .catch(() => {});
  }, []);

  // Group posts by subject, preserving insertion order of first appearance
  const subjects = [];
  const bySubject = {};
  posts.forEach((p) => {
    const key = p.subject || "General";
    if (!bySubject[key]) {
      subjects.push(key);
      bySubject[key] = [];
    }
    bySubject[key].push(p);
  });

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        dark ? "bg-gray-900" : "bg-gradient-to-b from-blue-50 to-emerald-50"
      }`}
    >
      {/* Hero */}
      <header className="text-center pt-20 pb-10 px-4">
        <h1 className={`text-4xl md:text-5xl font-bold ${dark ? "text-white" : "text-gray-900"}`}>
          Study Notes
        </h1>
        <p className={`mt-3 text-lg ${dark ? "text-gray-400" : "text-gray-500"}`}>
          Dive into a world of nursing, study, and caffeinated revelations.
        </p>
      </header>

      {/* Posts grouped by subject */}
      <main className="max-w-5xl mx-auto px-4 pb-20">
        {subjects.length === 0 && (
          <p className={`text-center py-16 text-lg ${dark ? "text-gray-600" : "text-gray-400"}`}>
            No notes yet — check back soon.
          </p>
        )}

        {subjects.map((subject) => (
          <section key={subject} className="mb-12">
            <h2
              className={`text-lg font-bold mb-4 pb-2 border-b uppercase tracking-wide
                ${dark
                  ? "text-emerald-400 border-gray-700"
                  : "text-emerald-700 border-emerald-200"
                }`}
            >
              {subject}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {bySubject[subject].map((p) => (
                <Link
                  key={p.slug}
                  to={`/study/${p.slug}`}
                  className={`group p-5 rounded-2xl border transition-all duration-200
                    hover:-translate-y-1 hover:shadow-lg
                    ${dark
                      ? "bg-gray-800 border-gray-700 hover:border-emerald-600"
                      : "bg-white border-gray-100 shadow-sm hover:border-emerald-300"
                    }`}
                >
                  <h3
                    className={`text-base font-semibold mb-2 leading-snug
                      group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors
                      ${dark ? "text-white" : "text-gray-900"}`}
                  >
                    {p.title}
                  </h3>
                  <p
                    className={`text-sm leading-relaxed line-clamp-3 ${
                      dark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {p.summary}
                  </p>
                  <div
                    className={`mt-3 text-xs ${dark ? "text-gray-600" : "text-gray-400"}`}
                  >
                    {new Date(p.published_at).toLocaleDateString()}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </main>

      {/* Footer */}
      <footer
        className={`border-t py-8 px-6 ${
          dark ? "border-gray-800 bg-gray-950" : "border-gray-100 bg-white"
        }`}
      >
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <p className={`font-semibold ${dark ? "text-gray-300" : "text-gray-700"}`}>
              5 Minute Nursing School
            </p>
            <p className={`text-sm ${dark ? "text-gray-500" : "text-gray-400"}`}>
              📍 Dhaka, Bangladesh
            </p>
          </div>
          <div className="flex gap-5">
            {SOCIAL.map(({ label, href, icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="text-2xl hover:scale-110 transition-transform"
              >
                {icon}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
